import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { HttpResponseEntity } from '../../../core/domain/common.models';
import { PinSettingData, PinValidationData } from '../../../core/domain/pin-creation-domain.models';
import { OtpRequestData, OtpValidationData } from '../../../core/domain/pin-otp-domain.models';
import { FSWalletPinTransactionRepository } from '../../../core/repository/fs-wallet-pin-transaction.repository';
import { HttpCheckPinRequest, HttpSetPinRequest } from './http-fs-wallet-pin-transaction.collection';

/**
 * HTTP Implementation of Unified Wallet PIN Transaction Repository
 * Clean Architecture Data Layer - Infrastructure Implementation
 *
 * Implements all PIN transaction operations in a single unified repository:
 * - OTP request/validation for PIN transactions
 * - PIN validation and setting operations
 *
 * All endpoints use the same API domain and base path:
 * /v1/internal/wallets/{wallet_id}/pin-transactions/
 */
@Injectable({ providedIn: 'root' })
export class HttpFSWalletPinTransactionRepository extends FSWalletPinTransactionRepository {
    private readonly api_base_url: string = environment.paper_mars_api_url;

    constructor(private http: HttpClient) {
        super();
    }

    // ========== OTP OPERATIONS ==========

    /**
     * Request OTP for PIN Transaction
     * Endpoint: POST /v1/internal/wallets/{wallet_id}/pin-transactions/request-otp
     *
     * @param data - Domain data
     * @returns Observable of domain entity
     */
    RequestWalletPinOtp(data: OtpRequestData): Observable<HttpResponseEntity> {
        const url = `${this.api_base_url}/internal/wallets/${data.wallet_id}/pin-transactions/request-otp`;

        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${data.token}`,
            company_id: data.company_id,
            user_id: data.user_id,
        });

        return this.http.post<HttpResponseEntity>(url, {}, { headers });
    }

    /**
     * Validate OTP for PIN Transaction
     * Endpoint: POST /v1/internal/wallets/{wallet_id}/pin-transactions/verify-otp
     *
     * @param data - Domain data
     * @returns Observable of domain entity
     */
    ValidateWalletPinOtp(data: OtpValidationData): Observable<HttpResponseEntity> {
        const url = `${this.api_base_url}/internal/wallets/${data.wallet_id}/pin-transactions/verify-otp`;

        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${data.token}`,
            company_id: data.company_id,
            user_id: data.user_id,
        });

        const payload = {
            otp: data.payload.otp,
        };

        return this.http.post<HttpResponseEntity>(url, payload, { headers });
    }

    // ========== PIN OPERATIONS ==========

    /**
     * Check PIN format and business rules
     * Endpoint: POST /v1/internal/wallets/{wallet_id}/pin-transactions/check-pin
     *
     * @param data - Domain data for PIN validation
     * @returns Observable of domain entity
     */
    CheckPin(data: PinValidationData): Observable<HttpResponseEntity> {
        const url = `${this.api_base_url}/internal/wallets/${data.wallet_id}/pin-transactions/check-pin`;

        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${data.token}`,
            company_id: data.company_id,
            user_id: data.user_id,
        });

        const payload: HttpCheckPinRequest = {
            pin: data.pin,
            action: data.action || 'create',
        };

        return this.http.post<HttpResponseEntity>(url, payload, { headers });
    }

    /**
     * Set PIN for wallet transactions
     * Endpoint: POST /v1/internal/wallets/{wallet_id}/pin-transactions/set-pin
     *
     * @param data - Domain data for PIN setting
     * @returns Observable of domain entity
     */
    SetPin(data: PinSettingData): Observable<HttpResponseEntity> {
        const url = `${this.api_base_url}/internal/wallets/${data.wallet_id}/pin-transactions/set-pin`;

        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${data.token}`,
            company_id: data.company_id,
            user_id: data.user_id,
        });

        const payload: HttpSetPinRequest = {
            pin: data.pin,
        };

        return this.http.post<HttpResponseEntity>(url, payload, { headers });
    }
}
